import { yupResolver } from '@hookform/resolvers/yup'
import { selectAddress } from 'feature/addresses/redux/addressCreateSelector'
import { AuthStatus, SmsStatus } from 'feature/auth/redux/authSlice'
import { setAuthRefreshAccessToken } from 'feature/auth/redux/authStorage'
import { RegistrationData, TSuggest } from 'feature/registration/types'
import { useEffect, useState } from 'react'
import { DefaultValues, Resolver, useForm, useWatch } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useActions, useAppSelector } from 'store/hooks'
import * as yup from 'yup'

import { applyAddressToMap, clearAddressState } from '../heplers/addressHelpers'

type StorageHandlers<TFormValues> = {
  get: () => TFormValues
  set: (values: TFormValues) => void
  delete: () => void
}

type UseRegistrationFormParams<TFormValues extends RegistrationData> = {
  schema: yup.AnyObjectSchema
  defaultValues: TFormValues
  storageHandlers: StorageHandlers<TFormValues>
  onSuccessRedirect: (context: { finish: boolean }) => void
  validationContext?: Record<string, unknown>
}

export const useRegistrationForm = <TFormValues extends RegistrationData>({
  schema,
  defaultValues,
  storageHandlers,
  onSuccessRedirect,
  validationContext,
}: UseRegistrationFormParams<TFormValues>) => {
  const navigate = useNavigate()
  const formMethods = useForm<TFormValues>({
    defaultValues: defaultValues as DefaultValues<TFormValues>,
    resolver: yupResolver(schema, {
      context: validationContext,
    }) as Resolver<TFormValues>,
    mode: 'all',
  })

  const [isExitModal, setIsExitModal] = useState(true)
  const [isLeaving, setIsLeaving] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [finish, setFinish] = useState(false)

  const { inputSuggest, input } = useAppSelector(selectAddress)
  const { authInfo } = useAppSelector(state => state.auth)
  const {
    clearMap,
    clearAddress,
    setInputAddress,
    setInputAddressSuggest,
    setIsValidAddress,
    setClickMarkCoords,
    setUserInfo,
    setAuthInfo,
    setSmsInfo,
  } = useActions()
  const watchFields = useWatch({ control: formMethods.control }) as TFormValues

  useEffect(() => {
    const storedValues = storageHandlers.get()
    formMethods.reset(storedValues)
    applyAddressToMap(storedValues.address, {
      setInputAddressSuggest,
      setIsValidAddress,
      setInputAddress,
      setClickMarkCoords,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isSubmitting || isLeaving) return

    const preparedValues = {
      ...watchFields,
      address:
        inputSuggest && inputSuggest.address === input ? inputSuggest : null,
    }

    storageHandlers.set(preparedValues as TFormValues)
  }, [
    watchFields,
    inputSuggest,
    input,
    isSubmitting,
    isLeaving,
    storageHandlers,
  ])

  useEffect(() => {
    if (authInfo.status === AuthStatus.registered) {
      setTimeout(() => {
        onSuccessRedirect({ finish })
      }, 0)
    }

    if (authInfo.status !== AuthStatus.smsConfirmed) {
      clearAddressState({ clearMap, clearAddress })
      storageHandlers.delete()
    }

    if (authInfo.status === AuthStatus.guest) {
      navigate('/login')
    }
  }, [authInfo.status, finish, onSuccessRedirect, navigate, clearAddressState])

  const handleSubmit = (submitFn: (values: TFormValues) => void) => {
    setIsExitModal(false)
    const currentValues = { ...formMethods.getValues() }

    currentValues.address = (inputSuggest as TSuggest) ?? currentValues.address

    setIsSubmitting(true)
    setFinish(true)
    submitFn(currentValues as TFormValues)
  }

  const handleConfirm = async () => {
    setIsLeaving(true)
    storageHandlers.delete()
    clearAddressState({ clearMap, clearAddress })
    setAuthRefreshAccessToken(null)
    setAuthInfo({
      status: AuthStatus.guest,
      accessToken: null,
      refreshToken: null,
    })
    setSmsInfo({ status: SmsStatus.readyForSend })
    setUserInfo({ phone: '' })
  }

  return {
    formMethods,
    isExitModal,
    handleSubmit,
    handleConfirm,
    authInfo,
  }
}
